import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { auditService } from '../../services/auditService';
import { PageLoader } from '../../components/Loader';
import { SCHEDULE_STATUS_LABEL, scheduleStatusOf } from '../../components/ScheduleStatus';
import GenerateReportDialog from './GenerateReportDialog';
import { auditDateRange } from './varianceReport';
import './ReportsList.css';

const VIEWS = [
  { id: 'month', label: 'Month' },
  { id: 'week', label: 'Week' },
  { id: 'day', label: 'Day' },
];

const STATUS_FILTERS = [
  { id: 'all', label: 'All' },
  { id: 'closed', label: 'Closed' },
  { id: 'in_progress', label: 'In progress' },
  { id: 'not_started', label: 'Upcoming' },
];

const pad = (n) => String(n).padStart(2, '0');
const dayKey = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

/** Monday of the date's week. */
function weekStart(date) {
  const d = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const shift = (d.getDay() + 6) % 7;
  d.setDate(d.getDate() - shift);
  return d;
}

const fmtDay = (d, opts = {}) => d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', ...opts });

/** Group key + label for a schedule's start date. */
function groupOf(audit, view) {
  const start = new Date(audit.start_at);
  if (Number.isNaN(start.getTime())) return { key: '0000', label: 'No date', sort: 0 };
  if (view === 'month') {
    return {
      key: `${start.getFullYear()}-${pad(start.getMonth() + 1)}`,
      label: start.toLocaleDateString('en-IN', { month: 'long', year: 'numeric' }),
      sort: new Date(start.getFullYear(), start.getMonth(), 1).getTime(),
    };
  }
  if (view === 'week') {
    const monday = weekStart(start);
    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);
    const sameYear = monday.getFullYear() === sunday.getFullYear();
    return {
      key: `w${dayKey(monday)}`,
      label: `Week of ${fmtDay(monday, sameYear ? {} : { year: 'numeric' })} – ${fmtDay(sunday, { year: 'numeric' })}`,
      sort: monday.getTime(),
    };
  }
  const day = new Date(start.getFullYear(), start.getMonth(), start.getDate());
  return {
    key: dayKey(day),
    label: `${day.toLocaleDateString('en-IN', { weekday: 'long' })}, ${day.toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })}`,
    sort: day.getTime(),
  };
}

function readView() {
  try {
    return localStorage.getItem('reportsView') || 'month';
  } catch {
    return 'month';
  }
}

/**
 * Reports: every schedule, grouped by month / week / day of its start, with
 * "Generate report" (styled variance report in the client's format).
 */
function ReportsHome() {
  const [audits, setAudits] = useState(null);
  const [error, setError] = useState('');
  const [view, setView] = useState(readView);
  const [search, setSearch] = useState('');
  const [client, setClient] = useState('');
  const [status, setStatus] = useState('all');
  const [collapsed, setCollapsed] = useState(() => new Set());
  const [generating, setGenerating] = useState(null);

  const load = useCallback(async () => {
    try {
      const list = await auditService.getAll();
      setAudits(Array.isArray(list) ? list : []);
      setError('');
    } catch (err) {
      setError(err.message);
      setAudits((current) => current || []);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const changeView = (next) => {
    setView(next);
    setCollapsed(new Set());
    try {
      localStorage.setItem('reportsView', next);
    } catch {
      // storage unavailable
    }
  };

  const clients = useMemo(
    () => [...new Set((audits || []).map((a) => a.organization_name).filter(Boolean))].sort((a, b) => a.localeCompare(b)),
    [audits],
  );

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return (audits || []).filter((a) => {
      if (client && a.organization_name !== client) return false;
      if (status !== 'all' && scheduleStatusOf(a) !== status) return false;
      if (!term) return true;
      return [a.schedule_no, a.name, a.organization_name, a.outlet_name, a.outlet_code, a.outlet_city]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
        .includes(term);
    });
  }, [audits, search, client, status]);

  const groups = useMemo(() => {
    const map = new Map();
    filtered.forEach((audit) => {
      const g = groupOf(audit, view);
      if (!map.has(g.key)) map.set(g.key, { ...g, audits: [] });
      map.get(g.key).audits.push(audit);
    });
    const list = [...map.values()].sort((a, b) => b.sort - a.sort);
    list.forEach((g) => g.audits.sort((a, b) => new Date(b.start_at) - new Date(a.start_at)));
    return list;
  }, [filtered, view]);

  const counts = useMemo(() => {
    const by = (s) => filtered.filter((a) => scheduleStatusOf(a) === s).length;
    return { total: filtered.length, closed: by('closed'), live: by('in_progress'), upcoming: by('not_started') };
  }, [filtered]);

  const toggle = (key) =>
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  return (
    <div className="page rl">
      <header className="rl-header">
        <div>
          <p className="page__eyebrow">Reports</p>
          <h1>Audit reports</h1>
          <p className="rl-muted">
            Every schedule by month, week or day. “Generate report” downloads the variance report (book → counted → 1st cut →
            reco → final difference) as a formatted Excel file.
          </p>
        </div>
      </header>

      <section className="rl-stats" aria-label="Schedules shown">
        <article>
          <span>Schedules</span>
          <strong>{counts.total.toLocaleString('en-IN')}</strong>
        </article>
        <article className="rl-stats--closed">
          <span>Closed</span>
          <strong>{counts.closed.toLocaleString('en-IN')}</strong>
        </article>
        <article className="rl-stats--live">
          <span>In progress</span>
          <strong>{counts.live.toLocaleString('en-IN')}</strong>
        </article>
        <article className="rl-stats--upcoming">
          <span>Upcoming</span>
          <strong>{counts.upcoming.toLocaleString('en-IN')}</strong>
        </article>
      </section>

      <div className="rl-toolbar">
        <div className="rl-seg" role="group" aria-label="Group by">
          {VIEWS.map((v) => (
            <button key={v.id} type="button" aria-pressed={view === v.id} onClick={() => changeView(v.id)}>
              {v.label}
            </button>
          ))}
        </div>
        <input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search schedule no, client, store…"
          aria-label="Search schedules"
        />
        <select value={client} onChange={(e) => setClient(e.target.value)} aria-label="Client">
          <option value="">All clients</option>
          {clients.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </select>
        <select value={status} onChange={(e) => setStatus(e.target.value)} aria-label="Status">
          {STATUS_FILTERS.map((s) => (
            <option key={s.id} value={s.id}>
              {s.label}
            </option>
          ))}
        </select>
        {groups.length > 1 ? (
          <button
            type="button"
            className="rl-link"
            onClick={() => setCollapsed(collapsed.size ? new Set() : new Set(groups.map((g) => g.key)))}
          >
            {collapsed.size ? 'Expand all' : 'Collapse all'}
          </button>
        ) : null}
      </div>

      {error ? <p className="rl-error">{error}</p> : null}

      {audits === null ? (
        <PageLoader label="Loading schedules…" />
      ) : groups.length === 0 ? (
        <p className="rl-empty">{audits.length ? 'No schedule matches these filters.' : 'No schedules yet.'}</p>
      ) : (
        <div className="rl-groups">
          {groups.map((group) => {
            const open = !collapsed.has(group.key);
            const closed = group.audits.filter((a) => scheduleStatusOf(a) === 'closed').length;
            return (
              <section key={group.key} className={`rl-group${open ? ' is-open' : ''}`}>
                <button type="button" className="rl-group__head" onClick={() => toggle(group.key)} aria-expanded={open}>
                  <span className="rl-group__chev" aria-hidden="true">
                    ›
                  </span>
                  <strong>{group.label}</strong>
                  <span className="rl-group__count">
                    {group.audits.length} schedule{group.audits.length === 1 ? '' : 's'}
                    {closed ? ` · ${closed} closed` : ''}
                  </span>
                </button>
                {open ? (
                  <ul className="rl-list">
                    {group.audits.map((audit) => {
                      const st = scheduleStatusOf(audit);
                      return (
                        <li key={audit.id} className="rl-row">
                          <div className="rl-row__main">
                            <div className="rl-row__title">
                              <code>{audit.schedule_no || '—'}</code>
                              <span className={`rl-status rl-status--${st}`}>{SCHEDULE_STATUS_LABEL[st] || st}</span>
                            </div>
                            <strong>{audit.name}</strong>
                          </div>
                          <div className="rl-row__meta">
                            <span className="rl-row__client">{audit.organization_name || '—'}</span>
                            <span>
                              {audit.outlet_code ? <b>{audit.outlet_code}</b> : null}
                              {audit.outlet_code && audit.outlet_name ? ' · ' : ''}
                              {audit.outlet_name || ''}
                              {audit.outlet_city ? `, ${audit.outlet_city}` : ''}
                            </span>
                          </div>
                          <div className="rl-row__date">{auditDateRange(audit) || '—'}</div>
                          <div className="rl-row__actions">
                            <Link className="rl-btn rl-btn--ghost" to={`/audits/${audit.id}/portal`}>
                              Open portal
                            </Link>
                            <button
                              type="button"
                              className="rl-btn rl-btn--primary"
                              onClick={() => setGenerating(audit)}
                              disabled={st === 'not_started'}
                              title={st === 'not_started' ? 'This schedule has not started yet' : 'Download the variance report'}
                            >
                              Generate report
                            </button>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                ) : null}
              </section>
            );
          })}
        </div>
      )}

      {generating ? <GenerateReportDialog audit={generating} onClose={() => setGenerating(null)} /> : null}
    </div>
  );
}

export default ReportsHome;
